import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";

import { catchError, debounceTime, map, switchMap, tap } from "rxjs/operators";

import {
  NgbHighlight,
  NgbTypeahead,
  NgbTypeaheadSelectItemEvent
} from "@ng-bootstrap/ng-bootstrap";
import { firstValueFrom, Observable, of, throwError } from "rxjs";

import { Plant } from "@models/Plant";
import { TypeaheadSearchResult } from "@models/TypeaheadSearchResult";

import { PlantsService } from "@services/plants.service";

import { BoldSearchPipe } from "@pipes/bold-search.pipe";
/**
 * Represents the Plant Quick Find component.
 * This component is responsible for searching and displaying plant results.
 */
@Component({
  selector: "plant-quick-find",
  imports: [NgbHighlight, FormsModule, NgbTypeahead, BoldSearchPipe],
  templateUrl: "plant-quick-find.component.html",
  styleUrls: ["plant-quick-find.component.scss"]
})
export class PlantQuickFindComponent implements OnInit {
  searchAheadText: string;
  isSearching = false;
  isLoading = false;
  hasSearchFailed = false;
  results: Plant[] = [];
  totalResults = 0;
  displayedResults = 0;
  hasSelectedResult = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedResult: any;
  noResults = false;

  typeAheadId: string | null = null;
  @ViewChild(NgbTypeahead) typeahead?: NgbTypeahead;

  constructor(
    private el: ElementRef,
    private plantsService: PlantsService,
    private renderer: Renderer2,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      const quickFindEl = this.el.nativeElement.querySelector("#find-plant");
      if (quickFindEl) {
        this.renderer.removeAttribute(quickFindEl, "aria-autocomplete");
      }
      this.typeAheadId = this.typeahead?.popupId;
      this.cdr.detectChanges();
    });
  }

  search = (text$: Observable<string>) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    self.isLoading = false;
    return text$.pipe(
      debounceTime(300),
      tap(() => {
        self.isSearching = true;
        self.isLoading = false;
      }),
      switchMap((term) => {
        self.searchAheadText = term;
        if (term.length === 0) {
          self.totalResults = 0;
          self.displayedResults = 0;
          self.noResults = false;
          return of([]);
        } else if (term.length >= 3) {
          return self.plantsService.searchAhead(term).pipe(
            tap(() => {
              self.hasSearchFailed = false;
              self.noResults = false;
            }),
            catchError((error) => {
              self.hasSearchFailed = true;
              self.noResults = true;
              self.isSearching = false;
              return throwError(() => error);
            })
          );
        } else {
          return of([]);
        }
      }),
      tap(() => (self.isSearching = false)),
      map((results) => {
        self.totalResults = results.length;
        const limitedResults = results.slice(0, 25);
        self.displayedResults = limitedResults.length;
        if (self.displayedResults === 0 && self.searchAheadText.length >= 3) {
          self.noResults = true;
        }
        return limitedResults;
      })
    );
  };

  inputFormatter = () =>
    this.selectedResult.Symbol +
    " " +
    this.selectedResult.ScientificName.replaceAll(/<\/?[i]>/gi, "");

  onSelect(event: NgbTypeaheadSelectItemEvent) {
    this.hasSelectedResult = true;
    this.selectedResult = event.item.Plant;
    this.totalResults = 0;
  }

  performSearch(): void {
    this.hasSelectedResult = false;
    this.isLoading = true;
    if (!this.searchAheadText) return;

    if (this.selectedResult) {
      this.router.navigate(["/plant-profile", this.selectedResult.Symbol]);
      return;
    }
    // reset results
    this.results = [];

    firstValueFrom(this.plantsService.searchAhead(this.searchAheadText))
      .then(this.displayResults.bind(this))
      .catch(() => (this.isLoading = false));
  }

  displayResults(results: TypeaheadSearchResult[]): void {
    this.router.navigate(["/plant-profile", results[0].Plant.Symbol]);
  }
}
